import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PaidPassRequiredModal from '../components/common/PaidPassRequiredModal';
import { defaultPlanFeature, defaultPlanFeatures, type PlanFeatureKey } from '../constants/planFeatures';
import { usePlanGateRows, type PlanGateRow } from '../hooks/usePlanGateRows';
import { PLUS_CANONICAL_PLAN_KEY } from '../services/planPermissionsService';
import { syncMySubscriptionPassState } from '../services/platformUserSubscriptionPassService';
import {
  getBoundSubscriptionPassUserId,
  getMaxClassesForPass,
  isFreePassOnly,
  isSubscriptionPassCacheVerified,
  SUBSCRIPTION_PASS_CHANGED_EVENT,
} from '../utils/subscriptionPassStorage';

export type PassGateStatus = 'pending' | 'ready' | 'error';

type PaidPassGateContextValue = {
  /** 서버 동기화 상태 — pending/error에서는 잠금 UI·리다이렉트를 확정하지 않는다 */
  passGateStatus: PassGateStatus;
  /** ready && 프리 이용권 — UI 잠금 표시용 */
  isPassLocked: boolean;
  /** @deprecated 잠금 표시는 isPassLocked / isFeatureLocked 사용. ready일 때만 의미 있음 */
  isFreePass: boolean;
  /** ready && 유료(등록) 이용권 */
  canUsePaidFeatures: boolean;
  /** 클래스 등록 상한 — pending/error는 프리 기준 */
  maxClasses: number;
  /** 미확정이면 차단(모달 없음). ready+프리면 모달 후 true */
  requirePaidPass: () => boolean;
  openPaidPassRequiredModal: () => void;
  /** 이용권별 기본 기능 권한(ready 일 때만 true). 표에 값이 없으면 기존 규칙(유료 = 사용) */
  canUseFeature: (key: PlanFeatureKey) => boolean;
  /** ready && 해당 기능 잠금 — 잠금 표시용 */
  isFeatureLocked: (key: PlanFeatureKey) => boolean;
  /** 기능 사용 전 검사. 미확정이면 차단(모달 없음), ready+잠금이면 모달 후 true */
  requireFeature: (key: PlanFeatureKey) => boolean;
};

const PaidPassGateContext = createContext<PaidPassGateContextValue | null>(null);

function tierFromBoundUser(): { status: PassGateStatus; isFree: boolean } {
  const uid = getBoundSubscriptionPassUserId();
  if (!uid) return { status: 'pending', isFree: true };
  if (!isSubscriptionPassCacheVerified(uid)) {
    return { status: 'pending', isFree: true };
  }
  const isFree = isFreePassOnly();
  return { status: 'ready', isFree };
}

/**
 * 현재 이용권의 권한표 행(FREE 또는 PLUS). 판정이 확실할 때만(ready) 쓰고,
 * 그 외(확인 중·마이그레이션 전·읽기 실패)는 null → 기존 고정 규칙.
 */
function resolvePlanRow(
  status: PassGateStatus,
  isFreeTier: boolean,
  rows: ReturnType<typeof usePlanGateRows>
): PlanGateRow | null {
  if (status !== 'ready' || !rows) return null;
  // PLUS 계열(베타·이벤트·개별·그룹)은 권한이 모두 같다 → 무료/PLUS 두 행만 본다(usePlanGateRows 가 PLUS 행을 하나로 합침)
  return (isFreeTier ? rows.free : rows[PLUS_CANONICAL_PLAN_KEY]) ?? null;
}

export function PaidPassGateProvider({ children }: { children: React.ReactNode }) {
  const [passGateStatus, setPassGateStatus] = useState<PassGateStatus>('pending');
  const [isFreeTier, setIsFreeTier] = useState(true);
  /** 같은 무료/유료 안에서 이용권 분류가 바뀌어도 다시 계산하도록 */
  const [passVersion, setPassVersion] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [boundUserId, setBoundUserId] = useState(() => getBoundSubscriptionPassUserId());

  const applyTier = useCallback((status: PassGateStatus, isFree: boolean) => {
    setPassGateStatus(status);
    setIsFreeTier(isFree);
    setPassVersion((v) => v + 1);
  }, []);

  const refreshFromCache = useCallback(() => {
    const uid = getBoundSubscriptionPassUserId();
    if (!uid) {
      applyTier('pending', true);
      return;
    }
    if (!isSubscriptionPassCacheVerified(uid)) {
      applyTier('pending', true);
      return;
    }
    applyTier('ready', isFreePassOnly());
  }, [applyTier]);

  const runServerSync = useCallback(async () => {
    const uid = getBoundSubscriptionPassUserId()?.trim();
    if (!uid) {
      applyTier('pending', true);
      return;
    }
    applyTier('pending', true);
    try {
      const result = await syncMySubscriptionPassState(uid);
      if (result.source === 'error') {
        if (isSubscriptionPassCacheVerified(uid)) {
          applyTier('ready', isFreePassOnly());
        } else {
          applyTier('error', true);
        }
        return;
      }
      applyTier('ready', isFreePassOnly());
    } catch (err) {
      console.error(err);
      if (isSubscriptionPassCacheVerified(uid)) {
        applyTier('ready', isFreePassOnly());
      } else {
        applyTier('error', true);
      }
    }
  }, [applyTier]);

  useEffect(() => {
    const onChanged = () => {
      setBoundUserId(getBoundSubscriptionPassUserId());
      refreshFromCache();
    };
    window.addEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(SUBSCRIPTION_PASS_CHANGED_EVENT, onChanged);
  }, [refreshFromCache]);

  useEffect(() => {
    if (!boundUserId) {
      applyTier('pending', true);
      return undefined;
    }
    void runServerSync();
    return undefined;
  }, [boundUserId, runServerSync, applyTier]);

  const openPaidPassRequiredModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  /**
   * 항상 최신 상태를 읽는 안정 함수(identity 고정).
   * 이전에는 [passGateStatus] 클로저라, useCallback([]) 등으로 첫 값을 붙잡은 호출부가
   * 'pending' 시점 함수를 계속 써서 영구 차단(모달도 없이 무반응)될 수 있었다.
   */
  const passGateStatusRef = React.useRef(passGateStatus);
  passGateStatusRef.current = passGateStatus;
  const requirePaidPass = useCallback(() => {
    if (passGateStatusRef.current !== 'ready') return true;
    if (!isFreePassOnly()) return false;
    setModalOpen(true);
    return true;
  }, []);

  const isPassLocked = passGateStatus === 'ready' && isFreeTier;
  const isFreePass = isPassLocked;
  const canUsePaidFeatures = passGateStatus === 'ready' && !isFreeTier;

  /** 이용권별 권한 관리(platform_plan_permissions): 기본 기능·최대 학급 수 */
  const planRows = usePlanGateRows();
  const planRow = useMemo(
    () => resolvePlanRow(passGateStatus, isFreeTier, planRows),
    // passVersion: 같은 무료/유료 안의 분류 변경 반영
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [passGateStatus, isFreeTier, planRows, passVersion]
  );
  const maxClasses = planRow?.maxClasses ?? getMaxClassesForPass(passGateStatus !== 'ready' || isFreeTier);

  const featureAllowed = useCallback(
    (key: PlanFeatureKey): boolean => {
      const v = planRow?.features[key];
      return typeof v === 'boolean' ? v : defaultPlanFeature(key, isFreeTier);
    },
    [planRow, isFreeTier]
  );
  const featureAllowedRef = React.useRef(featureAllowed);
  featureAllowedRef.current = featureAllowed;

  const canUseFeature = useCallback(
    (key: PlanFeatureKey) => passGateStatus === 'ready' && featureAllowed(key),
    [passGateStatus, featureAllowed]
  );
  const isFeatureLocked = useCallback(
    (key: PlanFeatureKey) => passGateStatus === 'ready' && !featureAllowed(key),
    [passGateStatus, featureAllowed]
  );
  const requireFeature = useCallback((key: PlanFeatureKey) => {
    if (passGateStatusRef.current !== 'ready') return true;
    if (featureAllowedRef.current(key)) return false;
    setModalOpen(true);
    return true;
  }, []);

  const value = useMemo<PaidPassGateContextValue>(
    () => ({
      passGateStatus,
      isPassLocked,
      isFreePass,
      canUsePaidFeatures,
      maxClasses,
      requirePaidPass,
      openPaidPassRequiredModal,
      canUseFeature,
      isFeatureLocked,
      requireFeature,
    }),
    [
      passGateStatus,
      isPassLocked,
      isFreePass,
      canUsePaidFeatures,
      maxClasses,
      requirePaidPass,
      openPaidPassRequiredModal,
      canUseFeature,
      isFeatureLocked,
      requireFeature,
    ]
  );

  return (
    <PaidPassGateContext.Provider value={value}>
      {children}
      <PaidPassRequiredModal open={modalOpen} onClose={closeModal} />
    </PaidPassGateContext.Provider>
  );
}

export function usePaidPassGate(): PaidPassGateContextValue {
  const ctx = useContext(PaidPassGateContext);
  if (!ctx) {
    const fallback = tierFromBoundUser();
    const ready = fallback.status === 'ready';
    const features = defaultPlanFeatures(fallback.isFree);
    return {
      passGateStatus: fallback.status,
      isPassLocked: ready && fallback.isFree,
      isFreePass: ready && fallback.isFree,
      canUsePaidFeatures: ready && !fallback.isFree,
      maxClasses: getMaxClassesForPass(!ready || fallback.isFree),
      requirePaidPass: () => !ready || fallback.isFree,
      openPaidPassRequiredModal: () => undefined,
      canUseFeature: (key) => ready && features[key],
      isFeatureLocked: (key) => ready && !features[key],
      requireFeature: (key) => !ready || !features[key],
    };
  }
  return ctx;
}
